import { Component, OnInit, OnDestroy, AfterViewInit, ElementRef, ViewChild } from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  Validators,
  AbstractControl,
  ValidationErrors,
  FormControl
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { WithdrawalService } from '../../_core/_services/withdrawal.service';
import { NotificationService } from '../../_core/_services/notification.service';
import { prettifyFieldKey } from '../../_core/_helpers/withdrawal-field.helper';
import {
  WithdrawalFormConfigResponseDto,
  WithdrawalBeneficiaryDto,
  ResolveWithdrawalDestinationResponseDto,
  FieldMetadataDto,
  LookupOptionSourceDto,
  ResolveBehaviorDto,
  SelectOptionDto,
  ValidationRulesDto,
  WithdrawalPreviewPayload
} from '../../_core/_model/withdrawal';
import { ApiService } from '../../_core/_services/api.service';
import { FormatService } from '../../_core/_services/format';
import { WalletDetailsDto } from '../../_core/_model/wallet';
import { WalletTierData } from '../../_core/_model/compliance';

@Component({
  selector: 'app-withdrawal-form',
  standalone: false,
  templateUrl: './withdrawal-form.html',
  styleUrl: './withdrawal-form.css',
})
export class WithdrawalForm implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('amountInput') amountInput?: ElementRef<HTMLInputElement>;

  private pendingFocusAmount = false;
  private destroy$ = new Subject<void>();

  walletId = '';
  loading = false;
  submitting = false;
  resolving = false;

  config: WithdrawalFormConfigResponseDto | null = null;
  walletData!: WalletDetailsDto;

  form!: FormGroup;
  destinationForm!: FormGroup;

  previewPayload: WithdrawalPreviewPayload | null = null;

  beneficiaries: WithdrawalBeneficiaryDto[] = [];
  selectedBeneficiaryId = '';
  resolutionResult: ResolveWithdrawalDestinationResponseDto | null = null;
  validationSummary: string[] = [];
  showValidationSummary = false;

  resolveError = '';
  resolveSuccess = '';
  bankSearch = '';

  remaining = 0;
  limit = 0;

  pageBlocker = '';
  pageBlockerCode = '';
  serverError = '';

  beneficiaryMode = false;

  private metadataMap: Record<string, FieldMetadataDto | null> = {};
  private validationMap: Record<string, ValidationRulesDto | null> = {};
  private optionsMap: Record<string, SelectOptionDto[]> = {};
  private lastResolvedSignature = '';
  private pendingSessionBeneficiary: WithdrawalBeneficiaryDto | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private withdrawalService: WithdrawalService,
    private notifyService: NotificationService,
    private api: ApiService,
    private fn: FormatService
  ) { }

  ngOnInit(): void {
    this.walletId = this.route.snapshot.paramMap.get('walletId') || '';
    this.buildBaseForm();
    this.loadWalletData();
    this.loadFormConfig();
    this.loadBeneficiaries();
  }

  ngAfterViewInit(): void {
    if (this.pendingFocusAmount) {
      this.focusAmountInput();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  buildBaseForm(): void {
    this.form = this.fb.group({
      amount: [null, [Validators.required]],
      paymentMethod: [''],
      saveBeneficiary: [false],
      beneficiaryNickname: ['']
    });

    this.destinationForm = this.fb.group({});

    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.showValidationSummary) {
          this.buildValidationSummary();
        }
      });

    this.form.get('saveBeneficiary')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((save: boolean) => {
        if (!save) return;

        const nicknameCtrl = this.form.get('beneficiaryNickname');
        if (!nicknameCtrl || nicknameCtrl.value) return;

        nicknameCtrl.setValue(this.getBeneficiaryDefaultNickname(), { emitEvent: false });
      });
  }

  loadWalletData(): void {
    if (!this.walletId) return;

    this.api.getSingleWallet(this.walletId).subscribe({
      next: (res) => {
        this.walletData = res.data;

        const rawRemainingWithdrawal = this.walletData.currencyRules?.usage?.remainingWithdrawal ?? 0;

        let limit = 0;

        const hasTierInfo =
          !!this.walletData?.tierInfo &&
          (
            (this.fn.getLimitData(this.walletData.tierInfo, 'Withdrawal', 'perTransactionLimit') ?? 0) > 0 ||
            (this.fn.getLimitData(this.walletData.tierInfo, 'Withdrawal', 'dailyLimit') ?? 0) > 0 ||
            (this.fn.getLimitData(this.walletData.tierInfo, 'Withdrawal', 'monthlyLimit') ?? 0) > 0
          );

        if (hasTierInfo) {
          const remainingDailyLimit = this.fn.getLimitData(this.walletData.tierInfo, 'Withdrawal', 'remainingDailyLimit') || 0;
          const remainingMonthlyLimit = this.fn.getLimitData(this.walletData.tierInfo, 'Withdrawal', 'remainingMonthlyLimit') || 0;
          const perTransactionLimit = this.fn.getLimitData(this.walletData.tierInfo, 'Withdrawal', 'perTransactionLimit') || 0;

          const candidates = [remainingDailyLimit, remainingMonthlyLimit, perTransactionLimit]
            .filter(x => typeof x === 'number' && x > 0);

          limit = candidates.length ? Math.min(...candidates) : 0;
          this.remaining = Math.max(0, limit);
        } else {
          limit = rawRemainingWithdrawal;
          this.remaining = Math.max(0, rawRemainingWithdrawal);
        }

        this.limit = Math.max(0, limit);
        this.applyAmountValidators();
      },
      error: (err) => {
        this.notifyService.showError(
          err?.error?.Error?.Details || err?.error?.message || 'Failed to load wallet limits',
          'Wallet Error'
        );
      }
    });
  }

  loadFormConfig(): void {
    if (!this.walletId) {
      this.notifyService.showError('Wallet ID is missing', 'Error');
      return;
    }

    this.loading = true;

    this.withdrawalService.getWithdrawalFormConfig(this.walletId).subscribe({
      next: (res) => {
        this.config = res.data;
        this.form.patchValue({
          paymentMethod: this.config?.paymentMethod || ''
        });
        this.buildDynamicFields();
        this.applyAmountValidators();
      },
      error: (err) => {
        this.notifyService.showError(
          err?.error?.Error?.Details || err?.error?.message || 'Failed to load withdrawal form config',
          'Error'
        );
        this.loading = false;
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  loadBeneficiaries(): void {
    if (!this.walletId) return;

    this.withdrawalService.getBeneficiaries(this.walletId).subscribe({
      next: (res) => {
        this.beneficiaries = res.data || [];

        this.loadSelectedBeneficiaryFromSession();
        this.tryApplyPendingSessionBeneficiary();
      },
      error: () => {
        this.beneficiaries = [];
      }
    });
  }

  private applyAmountValidators(): void {
    const minWithdraw = Number(
      this.config?.minWithdraw ||
      this.walletData?.currencyRules?.minWithdraw ||
      0
    );

    const availableBalance = Number(this.walletData?.availableBalance || 0);

    const candidates = [
      this.limit > 0 ? this.limit : null,
      availableBalance > 0 ? availableBalance : null
    ].filter((x): x is number => x !== null && x > 0);

    const maxAllowed = candidates.length ? Math.min(...candidates) : 0;

    const validators = [
      Validators.required,
      Validators.min(minWithdraw)
    ];

    if (maxAllowed > 0) {
      validators.push(Validators.max(maxAllowed));
    }

    this.form.get('amount')?.setValidators(validators);
    this.form.get('amount')?.updateValueAndValidity();

    if (this.showValidationSummary) {
      this.buildValidationSummary();
    }
  }

  buildDynamicFields(): void {
    const fields = this.config?.fields ?? [];
    const group: Record<string, FormControl> = {};

    this.metadataMap = {};
    this.validationMap = {};
    this.optionsMap = {};
    this.lastResolvedSignature = '';
    this.resolveError = '';
    this.resolveSuccess = '';

    for (const field of fields) {
      const validationJson = this.parseValidation(field.validationJson);
      const metadataJson = this.parseMetadata(field.metadataJson);
      const options = this.parseOptions(field.optionsJson);

      this.validationMap[field.fieldKey] = validationJson;
      this.metadataMap[field.fieldKey] = metadataJson;
      this.optionsMap[field.fieldKey] = options;

      const validators = [];

      if (field.isRequired) validators.push(Validators.required);
      if (validationJson?.minLength) validators.push(Validators.minLength(validationJson.minLength));
      if (validationJson?.maxLength) validators.push(Validators.maxLength(validationJson.maxLength));
      if (validationJson?.pattern) validators.push(Validators.pattern(validationJson.pattern));
      if (validationJson?.type === 'email') validators.push(Validators.email);

      group[field.fieldKey] = new FormControl(
        { value: '', disabled: !!metadataJson?.readonly },
        validators
      );
    }

    this.destinationForm = this.fb.group(group);

    if (this.hasAccountNumberAndConfirm()) {
      this.destinationForm.addValidators(this.accountNumberMatchValidator);
      this.destinationForm.updateValueAndValidity();
    }

    this.destinationForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.showValidationSummary) {
          this.buildValidationSummary();
        }
      });

    this.attachResolveWatchers();
    this.tryApplyPendingSessionBeneficiary();
  }

  private attachResolveWatchers(): void {
    const fields = this.config?.fields ?? [];

    for (const field of fields) {
      const metadata = this.metadataMap[field.fieldKey];
      const resolve = metadata?.resolve;

      if (!resolve?.triggerFields?.length || !resolve.resultField) {
        continue;
      }

      for (const triggerField of resolve.triggerFields) {
        const control = this.destinationForm.get(triggerField);
        if (!control) continue;

        control.valueChanges
          .pipe(
            debounceTime(500),
            distinctUntilChanged(),
            takeUntil(this.destroy$)
          )
          .subscribe(() => {
            if (this.shouldAutoResolve()) {
              this.clearResolvedField(resolve.resultField!);
              this.tryAutoResolve(resolve);
            }
          });
      }
    }

    const bankControl = this.destinationForm.get('bankCode');
    bankControl?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.bankSearch = '';
      });
  }

  private tryAutoResolve(resolve: ResolveBehaviorDto): void {
    if (!this.config || !resolve.triggerFields?.length || !resolve.resultField) {
      return;
    }

    const payload: Record<string, any> = {};

    for (const key of resolve.triggerFields) {
      const control = this.destinationForm.get(key);
      const value = control?.value;

      if (!value || String(value).trim() === '') {
        return;
      }

      if (control?.invalid) {
        return;
      }

      payload[key] = value;
    }

    const signature = JSON.stringify(payload);
    if (signature === this.lastResolvedSignature) {
      return;
    }

    this.resolving = true;
    this.resolveError = '';
    this.resolveSuccess = '';

    this.withdrawalService.resolveDestination({
      walletId: this.walletId,
      paymentMethod: this.config.paymentMethod || null,
      destinationDetailsJson: JSON.stringify(payload)
    }).subscribe({
      next: (res) => {
        this.lastResolvedSignature = signature;

        const resultControl = this.destinationForm.get(resolve.resultField!);
        if (resultControl) {
          resultControl.setValue(res.data?.accountName || '', { emitEvent: false });
        }

        if (res.data?.bankName && this.destinationForm.get('bankName')) {
          this.destinationForm.get('bankName')?.setValue(res.data.bankName, { emitEvent: false });
        }

        if (res.data?.isResolved && res.data?.accountName) {
          this.resolveSuccess = 'Account verified successfully.';
        } else {
          this.resolveError = res.data?.providerMessage || 'We could not verify this account yet.';
        }
      },
      error: (err) => {
        this.lastResolvedSignature = '';
        this.resolveSuccess = '';
        this.resolveError =
          err?.error?.Error?.Details ||
          err?.error?.message ||
          'We could not verify this account number for the selected bank.';

        const resultControl = this.destinationForm.get(resolve.resultField!);
        if (resultControl) {
          resultControl.setValue('', { emitEvent: false });
        }
        this.resolving = false;
      },
      complete: () => {
        this.resolving = false;
      }
    });
  }

  private clearResolvedField(fieldKey: string): void {
    const control = this.destinationForm.get(fieldKey);
    if (control) {
      control.setValue('', { emitEvent: false });
    }

    if (this.destinationForm.get('bankName')) {
      this.destinationForm.get('bankName')?.setValue('', { emitEvent: false });
    }

    this.lastResolvedSignature = '';
    this.resolveError = '';
    this.resolveSuccess = '';
  }

  onBankSearchChange(value: string): void {
    this.bankSearch = value || '';
  }

  getBankOptionCount(): number {
    const bankField = this.config?.fields?.find(x => x.fieldKey === 'bankCode');
    if (!bankField) return 0;
    return this.getFilteredOptions(bankField).length;
  }

  getFilteredOptions(field: { fieldKey: string }): SelectOptionDto[] {
    const options = this.getOptions(field);

    if (field.fieldKey !== 'bankCode') {
      return options;
    }

    const term = (this.bankSearch || '').trim().toLowerCase();
    if (!term) {
      return options;
    }

    return options.filter(x =>
      (x.label || '').toLowerCase().includes(term) ||
      (x.value || '').toLowerCase().includes(term)
    );
  }

  clearBankSearch(): void {
    this.bankSearch = '';
  }

  hasAccountNumberAndConfirm(): boolean {
    const fields = this.config?.fields ?? [];
    const keys = fields.map((x) => (x.fieldKey || '').toLowerCase());
    return keys.includes('accountnumber') && keys.includes('confirmaccountnumber');
  }

  accountNumberMatchValidator(group: AbstractControl): ValidationErrors | null {
    const acc = group.get('accountNumber')?.value;
    const confirm = group.get('confirmAccountNumber')?.value;

    if (!acc || !confirm) return null;
    return acc === confirm ? null : { accountNumberMismatch: true };
  }

  parseJson(value: unknown): any {
    if (!value) return null;
    if (typeof value === 'object') return value;

    try {
      return JSON.parse(String(value));
    } catch {
      return null;
    }
  }

  parseOptions(value: unknown): SelectOptionDto[] {
    const parsed = this.parseJson(value) as LookupOptionSourceDto | SelectOptionDto[] | null;

    if (!parsed) return [];

    if (Array.isArray(parsed)) {
      return parsed.map((x: any) => ({
        value: String(x?.value ?? x?.Value ?? x?.code ?? ''),
        label: String(x?.label ?? x?.Label ?? x?.name ?? x?.value ?? x?.Value ?? '')
      }));
    }

    if (Array.isArray((parsed as any).options)) {
      return (parsed as any).options.map((x: any) => ({
        value: String(x?.value ?? x?.Value ?? x?.code ?? ''),
        label: String(x?.label ?? x?.Label ?? x?.name ?? x?.value ?? x?.Value ?? '')
      }));
    }

    if (Array.isArray((parsed as any).Options)) {
      return (parsed as any).Options.map((x: any) => ({
        value: String(x?.Value ?? x?.value ?? x?.code ?? ''),
        label: String(x?.Label ?? x?.label ?? x?.name ?? x?.Value ?? x?.value ?? '')
      }));
    }

    return [];
  }

  parseValidation(value: unknown): ValidationRulesDto | null {
    return this.parseJson(value) as ValidationRulesDto | null;
  }

  parseMetadata(value: unknown): FieldMetadataDto | null {
    return this.parseJson(value) as FieldMetadataDto | null;
  }

  getOptions(field: { fieldKey: string }): SelectOptionDto[] {
    return this.optionsMap[field.fieldKey] || [];
  }

  isReadonly(field: { fieldKey: string }): boolean {
    return !!this.metadataMap[field.fieldKey]?.readonly;
  }

  isInvalid(ctrlName: string, group: 'base' | 'destination' = 'base'): boolean {
    const ctrl = group === 'base'
      ? this.form.get(ctrlName)
      : this.destinationForm.get(ctrlName);

    return !!ctrl && ctrl.invalid && (ctrl.dirty || ctrl.touched);
  }

  getErrorText(field: { fieldKey: string; label: string }): string {
    const ctrl = this.destinationForm.get(field.fieldKey);
    const rules = this.validationMap[field.fieldKey];

    if (!ctrl || !ctrl.errors) return '';

    if (ctrl.errors['required']) return `${field.label} is required.`;
    if (ctrl.errors['email']) return rules?.message || `${field.label} must be a valid email address.`;
    if (ctrl.errors['minlength']) return rules?.message || `${field.label} is too short.`;
    if (ctrl.errors['maxlength']) return rules?.message || `${field.label} is too long.`;
    if (ctrl.errors['pattern']) return rules?.message || `${field.label} format is invalid.`;

    return `${field.label} is invalid.`;
  }

  applyBeneficiary(item: WithdrawalBeneficiaryDto | undefined): void {
    if (!item) {
      return;
    }

    const details = this.parseJson(item.destinationDetailsJson);

    if (!details || typeof details !== 'object') {
      this.notifyService.showError(
        'The saved beneficiary details are invalid.',
        'Beneficiary'
      );
      return;
    }

    this.resolutionResult = null;
    this.resolveError = '';
    this.resolveSuccess = '';
    this.lastResolvedSignature = '';

    for (const field of this.config?.fields ?? []) {
      const control = this.destinationForm.get(field.fieldKey);

      if (!control) {
        continue;
      }

      const value = this.getBeneficiaryFieldValue(
        details,
        field.fieldKey
      );

      control.setValue(value ?? '', {
        emitEvent: false
      });
    }

    this.destinationForm.updateValueAndValidity({
      emitEvent: false
    });
  }

  private getBeneficiaryFieldValue(
    details: Record<string, any>,
    fieldKey: string
  ): any {
    if (details[fieldKey] !== undefined) {
      return details[fieldKey];
    }

    const aliases: Record<string, string[]> = {
      accountName: [
        'recipientName',
        'beneficiaryName',
        'account_name',
        'recipient_name',
        'beneficiary_name'
      ],

      recipientName: [
        'accountName',
        'beneficiaryName',
        'account_name',
        'recipient_name',
        'beneficiary_name'
      ],

      recipientEmail: [
        'email',
        'interacEmail',
        'recipient_email',
        'interac_email'
      ],

      email: [
        'recipientEmail',
        'interacEmail',
        'recipient_email',
        'interac_email'
      ],

      accountNumber: [
        'account_number'
      ],

      confirmAccountNumber: [
        'accountNumber',
        'account_number'
      ],

      bankCode: [
        'bank_code'
      ],

      bankName: [
        'bank_name'
      ],

      sortCode: [
        'sort_code'
      ],

      routingNumber: [
        'routing_number',
        'achRoutingNumber',
        'ach_routing_number'
      ],

      iban: [
        'IBAN'
      ]
    };

    for (const alias of aliases[fieldKey] ?? []) {
      if (
        details[alias] !== undefined &&
        details[alias] !== null &&
        details[alias] !== ''
      ) {
        return details[alias];
      }
    }

    return '';
  }

  useSelectedBeneficiary(): void {
    if (!this.selectedBeneficiaryId) {
      return;
    }

    if (!this.config || !this.destinationForm) {
      return;
    }

    const item = this.beneficiaries.find(
      x => x.id === this.selectedBeneficiaryId
    ) ?? (
        this.pendingSessionBeneficiary?.id ===
          this.selectedBeneficiaryId
          ? this.pendingSessionBeneficiary
          : undefined
      );

    if (!item) {
      this.notifyService.showError(
        'The selected beneficiary could not be found.',
        'Beneficiary'
      );
      return;
    }

    if (!item.isActive) {
      this.notifyService.showError(
        'The selected beneficiary is inactive.',
        'Beneficiary'
      );
      return;
    }

    this.beneficiaryMode = true;

    this.destinationForm.reset(
      {},
      { emitEvent: false }
    );

    this.applyBeneficiary(item);

    this.destinationForm.disable({
      emitEvent: false
    });

    this.form.patchValue(
      {
        saveBeneficiary: false,
        beneficiaryNickname: ''
      },
      {
        emitEvent: false
      }
    );

    this.resolveError = '';
    this.resolveSuccess =
      'Saved beneficiary loaded successfully.';
    this.lastResolvedSignature = '';

    if (this.showValidationSummary) {
      this.buildValidationSummary();
    }

    this.focusAmountInput();
  }

  resolveDestination(): void {
    this.resolveError = '';
    this.resolveSuccess = '';

    if (this.destinationForm.invalid) {
      this.destinationForm.markAllAsTouched();
      this.showValidationSummary = true;
      this.buildValidationSummary();
      return;
    }

    this.resolving = true;

    this.withdrawalService.resolveDestination({
      walletId: this.walletId,
      paymentMethod: this.config?.paymentMethod || null,
      destinationDetailsJson: JSON.stringify(this.destinationForm.getRawValue())
    }).subscribe({
      next: (res) => {
        this.resolutionResult = res.data;

        if (res.data?.accountName && this.destinationForm.get('accountName')) {
          this.destinationForm.get('accountName')?.setValue(res.data.accountName, { emitEvent: false });
        }

        if (res.data?.bankName && this.destinationForm.get('bankName')) {
          this.destinationForm.get('bankName')?.setValue(res.data.bankName, { emitEvent: false });
        }

        if (res.data?.isResolved) {
          this.resolveSuccess = res.data?.providerMessage || 'Destination resolved successfully.';
          this.resolveError = '';
        } else {
          this.resolveError = res.data?.providerMessage || 'Unable to resolve destination.';
          this.resolveSuccess = '';
        }

        if (this.showValidationSummary) {
          this.buildValidationSummary();
        }
      },
      error: (err) => {
        this.resolveError =
          err?.error?.Error?.Details ||
          err?.error?.message ||
          'Failed to resolve destination.';
        this.resolveSuccess = '';
        this.resolving = false;
      },
      complete: () => {
        this.resolving = false;
      }
    });
  }

  buildValidationSummary(): void {
    const errors: string[] = [];

    const amountCtrl = this.form.get('amount');
    if (amountCtrl?.errors?.['required']) {
      errors.push('Enter a withdrawal amount.');
    } else if (amountCtrl?.errors?.['min']) {
      const minWithdraw = Number(this.config?.minWithdraw || this.walletData?.currencyRules?.minWithdraw || 0);
      errors.push(`Minimum withdrawal is ${minWithdraw}.`);
    } else if (amountCtrl?.errors?.['max']) {
      errors.push(`Maximum allowed withdrawal is ${this.maxWithdrawableAmount}.`);
    } else if (amountCtrl?.invalid) {
      errors.push('Enter a valid withdrawal amount.');
    }

    if (!this.beneficiaryMode) {
      const fields = this.config?.fields ?? [];

      for (const field of fields) {
        const ctrl = this.destinationForm.get(field.fieldKey);

        if (ctrl?.invalid) {
          errors.push(this.getErrorText(field));
        }
      }

      if (this.destinationForm.errors?.['accountNumberMismatch']) {
        errors.push(
          'Account number and confirm account number do not match.'
        );
      }
    }

    this.validationSummary = [...new Set(errors)];
  }

  get availableBalance(): number {
    return Number(this.walletData?.availableBalance || 0);
  }

  get walletBalance(): number {
    return Number(this.walletData?.balance || 0);
  }

  get maxWithdrawableAmount(): number {
    const candidates = [
      this.limit > 0 ? this.limit : null,
      this.availableBalance > 0 ? this.availableBalance : null
    ].filter((x): x is number => x !== null && x > 0);

    return candidates.length ? Math.min(...candidates) : 0;
  }

  get hasBlockingError(): boolean {
    return !!this.pageBlocker;
  }

  getLimitData(
    tierInfo: WalletTierData | null,
    activityType: string,
    key: 'remainingDailyLimit' | 'remainingMonthlyLimit' | 'perTransactionLimit' | 'dailyLimit' | 'monthlyLimit'
  ) {
    if (!tierInfo) return 0;
    return this.fn.getLimitData(tierInfo, activityType, key) || 0;
  }

  checkLimit(activityType: string): boolean {
    const amount = Number(this.form.get('amount')?.value || 0);
    if (!amount) return false;

    const maxAllowed = this.maxWithdrawableAmount;

    if (maxAllowed > 0) {
      return amount <= maxAllowed;
    }

    return false;
  }

  canContinue(): boolean {
    const destinationIsValid =
      this.beneficiaryMode ||
      this.destinationForm.valid;

    return !this.submitting &&
      !this.resolving &&
      !this.hasBlockingError &&
      !!this.config &&
      !!this.walletData &&
      this.walletData.status === 'Active' &&
      this.availableBalance > 0 &&
      this.form.valid &&
      destinationIsValid &&
      this.checkLimit('Withdrawal');
  }

  continue(): void {
    if (this.submitting) {
      return;
    }

    this.showValidationSummary = true;
    this.form.markAllAsTouched();
    this.destinationForm.markAllAsTouched();
    this.buildValidationSummary();

    if (!this.canContinue() || !this.config) {
      return;
    }

    const amount = Number(this.form.value.amount || 0);
    const minWithdraw = Number(this.config.minWithdraw || this.walletData?.currencyRules?.minWithdraw || 0);

    if (amount < minWithdraw) {
      this.validationSummary = [`Minimum withdrawal is ${minWithdraw}.`];
      this.notifyService.showError(`Minimum withdrawal is ${minWithdraw}`, 'Validation');
      return;
    }

    if (this.maxWithdrawableAmount > 0 && amount > this.maxWithdrawableAmount) {
      this.validationSummary = [`Maximum allowed withdrawal is ${this.maxWithdrawableAmount}.`];
      this.notifyService.showError(`Maximum allowed withdrawal is ${this.maxWithdrawableAmount}`, 'Validation');
      return;
    }

    if (this.maxWithdrawableAmount <= 0) {
      this.validationSummary = ['You do not have any available balance for withdrawal.'];
      this.notifyService.showError('You do not have any available balance for withdrawal.', 'Validation');
      return;
    }

    const paymentMethod = this.config.paymentMethod || null;

    this.submitting = true;

    this.withdrawalService.quoteWithdrawal({
      walletId: this.walletId,
      amount,
      paymentMethod
    }).subscribe({
      next: (res) => {
        const preview: WithdrawalPreviewPayload = {
          walletId: this.walletId,
          amount,
          paymentMethod,
          destinationDetails: this.destinationForm.getRawValue(),
          config: this.config!,
          saveBeneficiary: !!this.form.value.saveBeneficiary,
          beneficiaryNickname: this.form.value.beneficiaryNickname || this.getBeneficiaryDefaultNickname() || null,
          resolutionResult: this.resolutionResult,
          quote: res.data
        };

        sessionStorage.setItem('withdrawal_preview', JSON.stringify(preview));

        this.router.navigate(['/wallets/withdraw/confirm', this.walletId]);
      },
      error: (err) => {
        const code = err?.error?.Error?.Code || err?.error?.error?.code || '';
        const message =
          err?.error?.Error?.Details ||
          err?.error?.message ||
          'Failed to retrieve withdrawal quote.';

        this.serverError = message;

        if (code === 'COMPLIANCE_BLOCK' || code === 'COUNTRY_CHANGE_COOLDOWN') {
          this.pageBlockerCode = code;
          this.pageBlocker = message;
        }

        this.notifyService.showError(message, 'Quote Error');
        this.submitting = false;
      },
      complete: () => {
        this.submitting = false;
      }
    });
  }

  submitWithdrawal(): void {
    // keep your existing implementation here
  }

  pretty(value: string): string {
    return prettifyFieldKey(value);
  }

  beneficiaryPreviewEntries(item: WithdrawalBeneficiaryDto): { key: string; value: string }[] {
    const data = this.parseJson(item.destinationDetailsJson) || {};
    return Object.keys(data).map(key => ({
      key,
      value: String(data[key] ?? '')
    }));
  }

  get selectedBeneficiary(): WithdrawalBeneficiaryDto | undefined {
    return this.beneficiaries.find(x => x.id === this.selectedBeneficiaryId);
  }

  clearSelectedBeneficiary(): void {
    this.selectedBeneficiaryId = '';
    this.beneficiaryMode = false;

    this.destinationForm.enable({
      emitEvent: false
    });

    this.destinationForm.reset({}, {
      emitEvent: false
    });

    // Restore controls configured as readonly.
    for (const field of this.config?.fields ?? []) {
      if (this.isReadonly(field)) {
        this.destinationForm
          .get(field.fieldKey)
          ?.disable({ emitEvent: false });
      }
    }

    this.resolveError = '';
    this.resolveSuccess = '';
    this.resolutionResult = null;
    this.lastResolvedSignature = '';

    if (this.showValidationSummary) {
      this.buildValidationSummary();
    }
  }

  focusAmountInput(): void {
    if (this.amountInput?.nativeElement) {
      this.amountInput.nativeElement.focus();
      this.pendingFocusAmount = false;
    } else {
      this.pendingFocusAmount = true;
    }
  }

  private shouldAutoResolve(): boolean {
    const method = String(this.config?.paymentMethod || '')
      .trim()
      .toLowerCase()
      .replace('-', '_');

    const hasBankCode = !!this.destinationForm.get('bankCode');

    return hasBankCode && method === 'bank_transfer';
  }

  getBeneficiaryDefaultNickname(): string {
    const destination = this.destinationForm?.getRawValue?.() || {};

    return (
      destination.accountName ||
      destination.recipientName ||
      destination.beneficiaryName ||
      destination.account_name ||
      destination.recipient_name ||
      destination.email ||
      destination.recipientEmail ||
      ''
    );
  }

  private loadSelectedBeneficiaryFromSession(): void {
    const raw = sessionStorage.getItem('selected_withdrawal_beneficiary');

    if (!raw) {
      return;
    }

    try {
      const beneficiary =
        JSON.parse(raw) as WithdrawalBeneficiaryDto;

      if (!beneficiary?.id) {
        this.clearBeneficiarySession();
        return;
      }

      if (beneficiary.walletId &&
        beneficiary.walletId !== this.walletId) {
        this.clearBeneficiarySession();
        return;
      }

      this.pendingSessionBeneficiary = beneficiary;
      this.tryApplyPendingSessionBeneficiary();
    } catch {
      this.clearBeneficiarySession();

      this.notifyService.showError(
        'The selected beneficiary data is invalid.',
        'Beneficiary'
      );
    }
  }
  private tryApplyPendingSessionBeneficiary(): void {
    if (!this.pendingSessionBeneficiary) {
      return;
    }

    if (!this.config || !this.destinationForm) {
      return;
    }

    const beneficiaryId =
      this.pendingSessionBeneficiary.id;

    const loadedBeneficiary =
      this.beneficiaries.find(x => x.id === beneficiaryId)
      ?? this.pendingSessionBeneficiary;

    if (!loadedBeneficiary.isActive) {
      this.clearBeneficiarySession();

      this.notifyService.showError(
        'The selected beneficiary is inactive.',
        'Beneficiary'
      );
      return;
    }

    this.selectedBeneficiaryId = loadedBeneficiary.id;
    this.pendingSessionBeneficiary = null;

    this.useSelectedBeneficiary();

    this.clearBeneficiarySession();
  }

  private clearBeneficiarySession(): void {
    sessionStorage.removeItem(
      'selected_withdrawal_beneficiary'
    );

    sessionStorage.removeItem(
      'selected_withdrawal_beneficiary_mode'
    );
  }
}